import {useEffect, useState} from 'react'
import {useForm} from 'react-hook-form'
import {useTranslation} from 'react-i18next'
import {useQueryClient} from '@tanstack/react-query'
import {Button} from '@/components/shadcn/button'
import {Separator} from '@/components/shadcn/separator'
import {ScrollArea} from '@/components/shadcn/scroll-area'
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/shadcn/dialog'
import {notify} from '@/lib/notify'
import type {GenericInAppNotificationDefinitionResponse} from '@/api/model/genericInAppNotificationDefinitionResponse'
import type {GenericInAppNotificationDefinitionRequest} from '@/api/model/genericInAppNotificationDefinitionRequest'
import type {InAppNotificationActionRequest} from '@/api/model/inAppNotificationActionRequest'
import type {InAppNotificationImageResponse} from '@/api/model/inAppNotificationImageResponse'
import {InAppNotificationActionType} from '@/api/model/inAppNotificationActionType'
import {LocalizationEntityType} from '@/api/model/localizationEntityType'
import {useEntityTranslations} from '@/components/common/translations/useEntityTranslations'
import {isActionValid} from '../dialog-components/deep-link/NotificationActionFields'
import {NotificationAttachments, type PendingImage} from '../dialog-components/NotificationAttachments'
import {NotificationContent} from '../dialog-components/NotificationContent'
import {NotificationPush} from '../dialog-components/NotificationPush'
import {NotificationLinking} from '../dialog-components/deep-link/NotificationLinking'
import {ModuleAssignmentFields} from '@/components/common/ModuleAssignmentFields'
import {AutoTranslateAllButton} from '@/components/common/translations/AutoTranslateAllButton'
import {triggerAutoTranslation, useResolvedLanguages} from '@/components/common/translations/useAutoTranslate'
import type {TranslationMap, PlannedLanguages} from '@/components/common/translations/types'
import {
  getGetApiCompaniesCurrentNotificationsQueryKey,
  useDeleteApiCompaniesCurrentNotificationsIdImageImageId,
  usePatchApiCompaniesCurrentNotificationsId,
  usePostApiCompaniesCurrentNotifications,
  usePostApiCompaniesCurrentNotificationsIdImage,
} from '@/api/generated/company-management-user/company-management-user.ts'

type FormValues = {
  title: string
  message: string
  isCompanyWideAssigned: boolean
  assignedGroupIds: number[]
  assignedEmployeeUserIds: string[]
  action: InAppNotificationActionRequest | null
  sendPushNotification: boolean
}

const defaultValues: FormValues = {
  title: '',
  message: '',
  isCompanyWideAssigned: false,
  assignedGroupIds: [],
  assignedEmployeeUserIds: [],
  action: null,
  sendPushNotification: false,
}

interface Props {
  isOpen: boolean
  onOpenChange: (open: boolean) => void
  notification?: GenericInAppNotificationDefinitionResponse | null
}

const TRANSLATABLE_PROPERTIES = ['title', 'message']

export function NotificationDialog({isOpen, onOpenChange, notification}: Props) {
  const isEdit = !!notification
  const {t} = useTranslation(['notifications', 'common'])
  const queryClient = useQueryClient()
  const languages = useResolvedLanguages()
  const {
    register, handleSubmit, reset, setValue, watch,
    formState: {isSubmitting},
  } = useForm<FormValues>({defaultValues})

  const [existingImages, setExistingImages] = useState<InAppNotificationImageResponse[]>([])
  const [pendingImages, setPendingImages] = useState<PendingImage[]>([])
  const [imageIdsToDelete, setImageIdsToDelete] = useState<string[]>([])
  const [submitAttempted, setSubmitAttempted] = useState(false)

  const [titleAutoTranslations, setTitleAutoTranslations] = useState<TranslationMap>({})
  const [messageAutoTranslations, setMessageAutoTranslations] = useState<TranslationMap>({})

  const [titlePlanned, setTitlePlanned] = useState<PlannedLanguages>(new Set())
  const [messagePlanned, setMessagePlanned] = useState<PlannedLanguages>(new Set())

  // When true: all non-manually-translated languages will be auto-translated on save.
  // The individual checkbox UI in TranslationsDialog is hidden in this mode.
  const [autoTranslateAll, setAutoTranslateAll] = useState(false)

  const translations = useEntityTranslations({
    entityType: LocalizationEntityType.GenericInAppNotification,
    entityId: notification ? String(notification.id) : undefined,
    properties: TRANSLATABLE_PROPERTIES,
    enabled: isOpen,
  })

  const {mutateAsync: create} = usePostApiCompaniesCurrentNotifications()
  const {mutateAsync: update} = usePatchApiCompaniesCurrentNotificationsId()
  const {mutateAsync: uploadImage} = usePostApiCompaniesCurrentNotificationsIdImage()
  const {mutateAsync: deleteImage} = useDeleteApiCompaniesCurrentNotificationsIdImageImageId()

  useEffect(() => {
    if (!isOpen) return
    reset(isEdit ? {
      title: notification.title ?? '',
      message: notification.message ?? '',
      isCompanyWideAssigned: notification.isCompanyWideAssigned ?? false,
      assignedGroupIds: (notification.assignedGroupIds ?? []) as number[],
      assignedEmployeeUserIds: (notification.assignedEmployeeUserIds ?? []) as string[],
      action: notification.action ? {
        type: notification.action.type ?? InAppNotificationActionType.ActionModule,
        actionText: notification.action.actionText ?? '',
        reference: notification.action.reference ?? undefined,
      } : null,
      sendPushNotification: notification.sendPushNotification ?? false,
    } : defaultValues)

    setExistingImages(notification?.images ?? [])
    setPendingImages((prev) => {
      prev.forEach((p) => URL.revokeObjectURL(p.previewUrl))
      return []
    })
    setImageIdsToDelete([])
    setSubmitAttempted(false)
    translations.reset()
    
    setTitleAutoTranslations({})
    setMessageAutoTranslations({})
    setTitlePlanned(new Set())
    setMessagePlanned(new Set())
    setAutoTranslateAll(false)
  }, [isOpen, notification])

  const invalidate = () =>
    queryClient.invalidateQueries({queryKey: getGetApiCompaniesCurrentNotificationsQueryKey()})

  const addPendingImage = (file: File) =>
    setPendingImages((prev) => [...prev, {file, previewUrl: URL.createObjectURL(file)}])

  const removeExistingImage = (imageId: string) => {
    setExistingImages((prev) => prev.filter((img) => img.id !== imageId))
    setImageIdsToDelete((prev) => [...prev, imageId])
  }

  const removePendingImage = (previewUrl: string) => {
    setPendingImages((prev) => prev.filter((img) => img.previewUrl !== previewUrl))
    URL.revokeObjectURL(previewUrl)
  }

  const handleOpenChange = (open: boolean) => {
    if (!open) pendingImages.forEach((img) => URL.revokeObjectURL(img.previewUrl))
    onOpenChange(open)
  }

  const onValid = async (values: FormValues) => {
    if (values.action && !isActionValid(values.action)) {
      setSubmitAttempted(true)
      return
    }

    const payload: GenericInAppNotificationDefinitionRequest = {
      title: values.title,
      message: values.message,
      isCompanyWideAssigned: values.isCompanyWideAssigned,
      assignedGroupIds: values.isCompanyWideAssigned ? [] : values.assignedGroupIds,
      assignedEmployeeUserIds: values.isCompanyWideAssigned ? [] : values.assignedEmployeeUserIds,
      action: values.action ?? null,
      sendPushNotification: values.sendPushNotification,
    }

    try {
      let notificationId: number
      if (notification) {
        await update({id: notification.id, data: payload})
        notificationId = notification.id
      } else {
        const res = await create({data: payload})
        notificationId = res.data as number
      }

      for (const pending of pendingImages) {
        await uploadImage({id: notificationId, data: {File: pending.file}})
        URL.revokeObjectURL(pending.previewUrl)
      }
      for (const imageId of imageIdsToDelete) {
        await deleteImage({id: notificationId, imageId})
      }

      await translations.save(String(notificationId))

      const manualTitleTranslations = translations.get('title')
      const manualMessageTranslations = translations.get('message')

      // If autoTranslateAll is active, send all language codes — the backend
      // skips any that already have a translation.
      const allLanguageCodes = languages.map((l) => l.code)

      triggerAutoTranslation({
        entityType: 'GenericInAppNotification',
        entityId: String(notificationId),
        properties: [
          {
            property: 'title',
            plannedLanguages: autoTranslateAll ? allLanguageCodes : [...titlePlanned],
            existingTranslations: {...titleAutoTranslations, ...manualTitleTranslations},
          },
          {
            property: 'message',
            plannedLanguages: autoTranslateAll ? allLanguageCodes : [...messagePlanned],
            existingTranslations: {...messageAutoTranslations, ...manualMessageTranslations},
          },
        ],
      })

      await invalidate()
      notify.success(notification ? t('toasts.updated') : t('toasts.created'))
      onOpenChange(false)
    } catch (error) {
      notify.apiError(error, t('toasts.saveError'))
    }
  }

  const titleValue = watch('title')
  const messageValue = watch('message')
  const companyWide = watch('isCompanyWideAssigned')
  const action = watch('action')
  const assignedGroupIds = watch('assignedGroupIds')
  const assignedEmployeeUserIds = watch('assignedEmployeeUserIds')
  const sendPush = watch('sendPushNotification')

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-300! h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>{notification ? t('dialog.editTitle') : t('dialog.createTitle')}</DialogTitle>
        </DialogHeader>

        <ScrollArea className="flex-1 min-h-0 pr-4">
          <form id="notification-form" onSubmit={handleSubmit(onValid)} className="flex gap-6 py-1">
            <div className="flex flex-1 flex-col gap-4">
              <NotificationContent
                titleField={register('title')}
                messageField={register('message')}
                titleTranslations={translations.get('title')}
                onTitleTranslationsChange={(value) => translations.set('title', value)}
                messageTranslations={translations.get('message')}
                onMessageTranslationsChange={(value) => translations.set('message', value)}
                titlePlannedLanguages={titlePlanned}
                onTitlePlannedChange={setTitlePlanned}
                messagePlannedLanguages={messagePlanned}
                onMessagePlannedChange={setMessagePlanned}
                autoTranslateAll={autoTranslateAll}
              />

              <NotificationPush
                sendPush={sendPush}
                onSendPushChange={(v) => setValue('sendPushNotification', v)}
              />

              <ModuleAssignmentFields
                isCompanyWide={companyWide}
                onCompanyWideChange={(v) => setValue('isCompanyWideAssigned', v)}
                selectedGroupIds={assignedGroupIds}
                onGroupsChange={(ids) => setValue('assignedGroupIds', ids)}
                selectedEmployeeIds={assignedEmployeeUserIds}
                onEmployeesChange={(ids) => setValue('assignedEmployeeUserIds', ids)}
              />
            </div>

            <Separator orientation="vertical" className="h-auto"/>

            <div className="flex flex-1 flex-col gap-4">
              <NotificationAttachments
                existingImages={existingImages}
                pendingImages={pendingImages}
                onAddFile={addPendingImage}
                onRemoveExisting={removeExistingImage}
                onRemovePending={removePendingImage}
              />
              <Separator/>
              <NotificationLinking
                action={action}
                onChange={(a) => setValue('action', a)}
                isCompanyWideAssigned={companyWide}
                groupIds={companyWide ? [] : assignedGroupIds}
                employeeUserIds={companyWide ? [] : assignedEmployeeUserIds}
                showError={submitAttempted}
              />
            </div>
          </form>
        </ScrollArea>

        <DialogFooter className="flex-row items-center gap-2">
          <AutoTranslateAllButton
            properties={[
              {
                property: 'title',
                sourceValue: titleValue,
                existingTranslations: titleAutoTranslations,
                manualTranslations: translations.get('title'),
              },
              {
                property: 'message',
                sourceValue: messageValue,
                existingTranslations: messageAutoTranslations,
                manualTranslations: translations.get('message'),
              },
            ]}
            autoTranslateAll={autoTranslateAll}
            onAutoTranslateAllChange={setAutoTranslateAll}
          />

          <div className="ml-auto flex gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => handleOpenChange(false)}
              disabled={isSubmitting}
            >
              {t('common:buttons.cancel')}
            </Button>
            <Button type="submit" form="notification-form" disabled={isSubmitting}>
              {isSubmitting ? t('common:buttons.saving') : t('common:buttons.save')}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
